import type { TFunction } from 'i18next';
import { ERROR_CODES, CODED_ERROR_DETAIL_SEP, type ErrorCode } from '@zsp/shared';

const KNOWN: ReadonlySet<string> = new Set(ERROR_CODES);

/** Max characters of upstream detail to surface in the toast — ElevenLabs can
 *  return long bodies and a 2KB string in a sonner toast looks broken. The full
 *  body remains in the server logs. */
const DETAIL_MAX = 300;

/** Reads the coded error message from a ZenStack fetcher rejection. The server
 *  may encode `<CODE> :: <detail>` in the message (see `CodedError`) — we split
 *  here so the UI can show both the canonical translation and the upstream
 *  reason. Falls back to UNKNOWN_ERROR if the prefix isn't a known code. */
function parseInfoMessage(err: unknown): { code: ErrorCode; detail?: string } {
  const info = (err as { info?: { message?: unknown } } | null)?.info;
  const candidate =
    (typeof info?.message === 'string' ? info.message : undefined) ??
    (err instanceof Error ? err.message : undefined);
  if (!candidate) return { code: 'UNKNOWN_ERROR' };
  const sepIdx = candidate.indexOf(CODED_ERROR_DETAIL_SEP);
  const codePart = sepIdx >= 0 ? candidate.slice(0, sepIdx) : candidate;
  const rawDetail = sepIdx >= 0 ? candidate.slice(sepIdx + CODED_ERROR_DETAIL_SEP.length) : '';
  const detail = rawDetail.trim().slice(0, DETAIL_MAX) || undefined;
  if (KNOWN.has(codePart)) return { code: codePart as ErrorCode, detail };
  return { code: 'UNKNOWN_ERROR' };
}

export function translateError(t: TFunction, err: unknown): string {
  const { code, detail } = parseInfoMessage(err);
  const base = t(`errors.${code}`);
  return detail ? `${base}\n${detail}` : base;
}

/** Variant for surfaces that render title + body separately (e.g. sonner
 *  toasts). `description` is the upstream `detail` (the provider's rejection reason),
 *  which doesn't have a natural i18n translation. */
export function translateErrorParts(
  t: TFunction,
  err: unknown,
): { title: string; description?: string } {
  const { code, detail } = parseInfoMessage(err);
  return { title: t(`errors.${code}`), description: detail };
}
